import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import { setReservedZone, clearReservedZone, getReservedZonesExcept } from './reservedZones';
import {
  INTERACTIVE_SELECTOR, probePoints, resolveBlockedZones, shouldRemeasureAfterMutation,
} from './interactiveOverlap';
import { AVATAR_SIZE, type Insets, type Point, type Rect, type Size } from './widgetPosition';

/**
 * CORE-1120 — живая проверка того, на чём стоит иконка.
 *
 * Спрашиваем у браузера, что под кружком, находим активные элементы и кладём их
 * в реестр занятых областей. Дальше работает та же геометрия, что обходит
 * шапку, — второго способа двигать иконку не заводим.
 *
 * Веб-только по существу, а не из осторожности: спросить «что под точкой» умеет
 * DOM, и на нативе такого вопроса задать некому. Там иконка и так прижата к
 * углу своей четверти. Инвариант I-2 соблюдён: `document` живёт внутри ветки
 * `Platform.OS === 'web'` и в общий код не протекает.
 *
 * Считаем от БАЗОВОЙ позиции — той, что до обхода. Считать от итоговой значило
 * бы зациклиться: иконка ушла бы с кнопки, перекрытие пропало, зона снялась,
 * иконка вернулась на кнопку — и так каждый кадр. База от результата не
 * зависит, поэтому ответ устойчив, а движение однократно.
 *
 * Но щупать ОДНУ ЛИШЬ базу мало, и это показал QA FAIL 04.09.2026 на `/masters`
 * (390×844). Иконку перенесли на фильтр «Все мастера», она честно ушла с него
 * вверх — и села на поле поиска, которое стоит строкой выше. Обход не отказал:
 * геометрия вывела иконку ровно из той зоны, которую ей показали, и поставила в
 * точку, о которой не знала ничего. Место назначения не щупал никто.
 *
 * Поэтому кругов несколько (см. {@link MAX_PROBE_ROUNDS}): щупаем базу →
 * считаем той же геометрией, куда уйдёт иконка → щупаем НАЗНАЧЕНИЕ → найденное
 * там добавляем в список и считаем заново. Устойчивость от этого не страдает:
 * список зон на каждом круге только растёт, а точка отсчёта всё та же база.
 * Ответ остаётся функцией страницы и базы, а не текущей отрисованной позиции.
 *
 * КОГДА щупаем — вторая половина той же задачи, и её показал QA FAIL на staging
 * (02.09.2026, `/dashboard#rooms`, 390×844: иконка на «Создать Live-сессию»).
 * Замер по входу на экран, по размерам окна и по смене маршрута видит только то,
 * что уже есть на странице в эту секунду. Содержимое вкладки приезжает из сети
 * позже, а сами вкладки профиля переключаются через `history.pushState` — ни
 * маршрута, ни размеров, ни базовой позиции это не меняет. Кнопка появляется
 * ПОД иконкой, и ни одного повода перемерить при этом не возникает.
 *
 * Поэтому поводом стало изменение самой страницы. Два правила, которые держат
 * это в рамках, вынесены в `shouldRemeasureAfterMutation`: изменения внутри
 * виджета не считаются (иначе замер запускал бы сам себя), и изменения сразу
 * за прокруткой тоже (иначе вернулась бы «иконка прыгает при скролле»).
 */

const ZONE_ID = 'hit-test-block';

/** Столько ждём после повода. Достаточно, чтобы не мерить каждый кадр. */
const SETTLE_MS = 160;

/**
 * Потолок откладывания. Страница, которая меняется без остановки (лента,
 * счётчик, догружающийся список), сдвигала бы отложенный замер вечно — и он не
 * случился бы ни разу. Здесь ожидание обрывается: лучше померить по ещё не
 * успокоившейся странице, чем не померить совсем.
 */
const MAX_SETTLE_MS = 1200;

/** Элементы виджета в расчёт не идут — иначе он всегда находил бы сам себя. */
const WIDGET_LAYER_ID = 'agapella-widget-layer';

/** Владелец записи в реестре — модульный: хук в приложении ровно один. */
const OWNER = {};

export interface InteractiveOverlapParams {
  /** Позиция до обхода зон. */
  base: Point;
  viewport: Size;
  /** Те же края, что у геометрии: круг считает назначение ровно её правилами. */
  insets?: Insets;
  margin?: number;
  size?: number;
  /** Смена маршрута: страница другая, мерить надо заново. */
  routeKey?: string;
  /** Во время перетаскивания не мешаем, и с открытым окном тоже. */
  enabled: boolean;
}

export function useInteractiveOverlap(params: InteractiveOverlapParams): void {
  // Умолчания для insets/margin не нужны: их знает `resolveBlockedZones`, и
  // держать вторую копию значило бы завести второй источник правды о краях.
  const { base, viewport, insets, margin, size = AVATAR_SIZE, routeKey, enabled } = params;

  // Слушатели ставятся один раз и читают свежие значения отсюда. Пересоздавать
  // их на каждое изменение позиции нельзя: снятие зоны в уборке эффекта на миг
  // возвращало бы иконку на кнопку, с которой она только что ушла.
  const latest = useRef(params);
  latest.current = { ...params, size };

  const scheduleRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    // jsdom и серверный рендер такого вопроса не понимают — тогда живой проверки
    // просто нет, а объявленные зоны продолжают работать.
    if (typeof document.elementsFromPoint !== 'function') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    /** Когда появился первый неотработанный повод — см. MAX_SETTLE_MS. */
    let pendingSince = 0;
    /** Когда страницу последний раз прокручивали — см. SCROLL_QUIET_MS. */
    let scrolledAt = 0;
    /** Сколько зон опубликовано сейчас — чтобы снять лишние на следующем замере. */
    let published = 0;

    /**
     * Зоны публикуются по одной, а не одним объединяющим прямоугольником: см.
     * {@link usableZones}. Реестр адресует запись именем, поэтому имён нужно
     * столько же, сколько зон, и хвост прошлого замера надо снять явно.
     */
    const publish = (rects: readonly Rect[]) => {
      rects.forEach((rect, index) => setReservedZone(`${ZONE_ID}/${index}`, rect, OWNER));
      for (let index = rects.length; index < published; index += 1) {
        clearReservedZone(`${ZONE_ID}/${index}`, OWNER);
      }
      published = rects.length;
    };

    const measure = () => {
      timer = null;
      const p = latest.current;
      if (!p.enabled) {
        publish([]);
        return;
      }

      const size = p.size ?? AVATAR_SIZE;

      /** Единственное, ради чего здесь нужен браузер: что лежит под кружком. */
      const probe = (anchor: Point): Rect[] => {
        const rects: Rect[] = [];
        const seen = new Set<Element>();
        for (const point of probePoints(anchor, size)) {
          for (const el of document.elementsFromPoint(point.x, point.y)) {
            if (el.closest(`#${WIDGET_LAYER_ID}`)) continue;
            const control = el.closest(INTERACTIVE_SELECTOR);
            if (!control || seen.has(control)) continue;
            seen.add(control);
            // Координаты viewport — те же, в которых живёт иконка: слой виджета
            // `position: fixed`, и `getBoundingClientRect` отсчитывается оттуда же.
            const r = control.getBoundingClientRect();
            rects.push({ x: r.left, y: r.top, width: r.width, height: r.height });
          }
        }
        return rects;
      };

      publish(resolveBlockedZones({
        base: p.base,
        // Чужие зоны — вход геометрии наравне с находками замера: иконка обходит
        // шапку и объявленные экраном области так же, и без них круг посчитал бы
        // назначение не туда, куда она на самом деле уедет.
        declared: getReservedZonesExcept(OWNER),
        viewport: p.viewport,
        insets: p.insets,
        margin: p.margin,
        size,
        probe,
      }));
    };

    const schedule = () => {
      const now = Date.now();
      if (timer == null) {
        pendingSince = now;
      } else {
        clearTimeout(timer);
        // Поводы идут подряд и каждый отодвигает замер. Через MAX_SETTLE_MS
        // после первого из них мерим по тому, что есть: иначе непрерывно
        // меняющаяся страница не измерялась бы никогда.
        if (now - pendingSince >= MAX_SETTLE_MS) {
          measure();
          return;
        }
      }
      timer = setTimeout(measure, SETTLE_MS);
    };
    scheduleRef.current = schedule;

    // Прокрутка замер НЕ запускает — намеренно.
    //
    // Раньше здесь висел `scroll` с `capture`, и замер повторялся на каждой
    // паузе в прокрутке. На телефоне под иконкой непрерывно проезжают кнопки
    // карточек: зона то появляется, то исчезает, и иконка прыгала вверх-вниз
    // всю дорогу (владелец на мобильном вебе: «двигается вверх-вниз при
    // скролле»). Замер по прокрутке не защищает от перекрытия — он гоняет
    // иконку за контентом, который всё равно уедет.
    //
    // Отметку времени прокрутка всё-таки оставляет: наблюдатель ниже смотрит
    // на изменения страницы, а прокрутка длинного списка меняет DOM не меньше,
    // чем загрузка. `shouldRemeasureAfterMutation` по этой отметке и отличает
    // «страница стала другой» от «контент проехал под иконкой».
    const markScroll = () => { scrolledAt = Date.now(); };
    window.addEventListener('scroll', markScroll, { passive: true, capture: true });

    // CORE-1120, QA FAIL 02.09.2026 на staging — «Создать Live-сессию» на
    // `/dashboard#rooms`. Замер на входе эту кнопку не видел: её ещё не было.
    // Содержимое вкладки приезжает из сети ПОСЛЕ замера, а переключение вкладок
    // профиля идёт через `history.pushState` — ни маршрут, ни размеры окна, ни
    // базовая позиция при этом не меняются, то есть ни одного прежнего повода
    // перемерить не возникает вовсе. Иконка так и остаётся стоять на кнопке,
    // которая появилась под ней.
    //
    // Поэтому поводом становится само изменение страницы. Наблюдатель дешёвый:
    // он лишь ставит в очередь тот же отложенный замер, а решение «считать ли
    // это поводом» вынесено в чистую функцию рядом.
    const observer = typeof MutationObserver === 'function'
      ? new MutationObserver((records) => {
        const msSinceScroll = Date.now() - scrolledAt;
        for (const record of records) {
          const node = record.target;
          const el = node.nodeType === 1 ? (node as Element) : node.parentElement;
          if (!shouldRemeasureAfterMutation({
            insideWidgetLayer: !!el?.closest(`#${WIDGET_LAYER_ID}`),
            msSinceScroll,
          })) continue;
          schedule();
          return;
        }
      })
      : null;
    observer?.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', schedule, { passive: true });
    schedule();

    return () => {
      if (timer != null) clearTimeout(timer);
      observer?.disconnect();
      window.removeEventListener('scroll', markScroll, { capture: true });
      window.removeEventListener('resize', schedule);
      publish([]);
    };
  }, []);

  // Позиция, окно, маршрут или разрешение изменились — перемерить. Не сразу:
  // сменившаяся страница ещё не разложилась, мерить пока нечего.
  useEffect(() => {
    scheduleRef.current();
  }, [
    base.x, base.y, viewport.width, viewport.height, size, routeKey, enabled,
    // Края входят в расчёт назначения — их изменение такой же повод перемерить,
    // как и смена размера окна.
    insets?.top, insets?.right, insets?.bottom, insets?.left, margin,
  ]);
}

export { WIDGET_LAYER_ID, ZONE_ID as HIT_TEST_ZONE_ID, SETTLE_MS, MAX_SETTLE_MS };
